import React, { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Row,
  Col,
  Image,
  List,
  Spin,
  message,
  Button,
  Divider,
  Typography,
  Space,
  Collapse
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  KeyOutlined
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next"; // ✅ i18n
import axiosInstance from "../../../services/server/axios";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(); // ✅ useTranslation
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/mes-achats/${id}`, {
        headers: getAuthHeaders(),
      });
      
      if (response.data.success) {
        setOrder(response.data.data);
      } else {
        message.error(t("Error loading order details"));
      }
    } catch (error) {
      console.error("Erreur:", error);
      message.error(t("Server connection error"));
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirme':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'enattente':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case 'annule':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 'confirme':
        return <Tag color="green" icon={getStatusIcon(status)}>{t("Confirmed")}</Tag>;
      case 'enattente':
        return <Tag color="orange" icon={getStatusIcon(status)}>{t("Pending")}</Tag>;
      case 'annule':
        return <Tag color="red" icon={getStatusIcon(status)}>{t("Cancelled")}</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={4}>{t("Order not found")}</Title>
          <Button type="primary" onClick={() => navigate('/client-dashboard/orders')}>
            {t("Back to orders")}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate('/client-dashboard/orders')}
        style={{ marginBottom: 16 }}
      >
        {t("Back to orders")}
      </Button>

      <Card>
        <Row gutter={16}>
          <Col xs={24} md={16}>
            <Title level={2}>{t("Order details")} #{order.id}</Title>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Descriptions bordered column={1}>
                <Descriptions.Item label={t("Status")}>
                  {getStatusTag(order.etat)}
                </Descriptions.Item>
                <Descriptions.Item label={t("Purchase date")}>
                  <CalendarOutlined /> {formatDate(order.date_achat)}
                </Descriptions.Item>
                <Descriptions.Item label={t("Amount")}>
                  <DollarOutlined /> {order.montant || "N/A"} €
                </Descriptions.Item>
                {order.login && (
                  <Descriptions.Item label={t("Credentials")}>
                    <KeyOutlined /> {order.login}
                    {order.password && ` / ${order.password}`}
                  </Descriptions.Item>
                )}
              </Descriptions>

              {order.offre && (
                <Card title={t("Offer details")} size="small">
                  <Descriptions column={1}>
                    <Descriptions.Item label={t("Title")}>
                      {order.offre.titre}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Description")}>
                      {order.offre.description}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Original price")}>
                      {order.offre.prix} €
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              )}

              {order.achatImages && order.achatImages.length > 0 && (
                <Card title={t("Associated images")} size="small">
                  <Row gutter={[16, 16]}>
                    {order.achatImages.map((image, index) => (
                      <Col xs={12} sm={8} md={6} key={index}>
                        <Image
                          src={image.url}
                          alt={`${t("Image")} ${index + 1}`}
                          style={{ borderRadius: 8 }}
                        />
                      </Col>
                    ))}
                  </Row>
                </Card>
              )}
            </Space>
          </Col>

          <Col xs={24} md={8}>
            <Card title={t("Summary")} size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>{t("Reference")}:</Text>
                  <br />
                  <Text>CMD-{order.id}</Text>
                </div>
                <Divider style={{ margin: '12px 0' }} />
                <div>
                  <Text strong>{t("Order date")}:</Text>
                  <br />
                  <Text>{formatDate(order.created_at)}</Text>
                </div>
                <Divider style={{ margin: '12px 0' }} />
                <div>
                  <Text strong>{t("Last update")}:</Text>
                  <br />
                  <Text>{formatDate(order.updated_at)}</Text>
                </div>
              </Space>
            </Card>

            {order.payment && (
              <Card title={t("Payment")} size="small" style={{ marginTop: 16 }}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label={t("Method")}>
                    {order.payment.methode}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("Status")}>
                    <Tag color={order.payment.statut === 'complet' ? 'green' : 'orange'}>
                      {order.payment.statut}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("Date")}>
                    {formatDate(order.payment.date_paiement)}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}
          </Col>
        </Row>

        <Collapse style={{ marginTop: 24 }} ghost>
          {order.reglements && order.reglements.length > 0 && (
            <Panel header={t("Payment history")} key="1">
              <List
                size="small"
                dataSource={order.reglements}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={`${t("Payment")} #${item.id}`}
                      description={
                        <div>
                          <div>{t("Amount")}: {item.montant} €</div>
                          <div>{t("Date")}: {formatDate(item.date_reglement)}</div>
                          <div>{t("Method")}: {item.methode}</div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Panel>
          )}

          {order.backups && order.backups.length > 0 && (
            <Panel header={t("Backups")} key="2">
              <List
                size="small"
                dataSource={order.backups}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={`${t("Backup from")} ${formatDate(item.date_sauvegarde)}`}
                      description={
                        <div>
                          <div>{t("Type")}: {item.type}</div>
                          <div>{t("Size")}: {item.taille}</div>
                        </div>
                      }
                    />
                    <Button type="link" size="small">
                      {t("Download")}
                    </Button>
                  </List.Item>
                )}
              />
            </Panel>
          )}
        </Collapse>
      </Card>
    </div>
  );
};

export default OrderDetail;
